import { useEffect } from 'react';
import FileUpload from './FileUpload';
import FileList from './FileList';
import { useFileUpload } from '../../hooks/useFileUpload';

interface FileManagerProps {
  entityType: 'request' | 'construction' | 'board' | 'talkroom' | 'daily-report' | 'user' | 'company';
  entityId?: string;
  title?: string;
  accept?: string;
  multiple?: boolean;
  maxSize?: number;
  maxFiles?: number;
  layout?: 'list' | 'grid';
  showUpload?: boolean;
  showDelete?: boolean;
  showDownload?: boolean;
  showPreview?: boolean;
  disabled?: boolean;
  className?: string;
  onFilesChange?: (files: Array<{ id: string; url: string; fileName: string }>) => void;
}

export default function FileManager({
  entityType,
  entityId,
  title = '添付ファイル',
  accept = '*/*',
  multiple = true,
  maxSize = 10 * 1024 * 1024,
  maxFiles = 10,
  layout = 'list',
  showUpload = true,
  showDelete = true,
  showDownload = true,
  showPreview = true,
  disabled = false,
  className = '',
  onFilesChange,
}: FileManagerProps) {
  const {
    files,
    isUploading,
    isLoading,
    error,
    uploadFiles,
    deleteFile,
    fetchFiles,
    clearError,
  } = useFileUpload({
    entityType,
    entityId,
    onSuccess: (uploadedFiles) => {
      if (onFilesChange) {
        onFilesChange(uploadedFiles.map(f => ({
          id: f.id,
          url: f.url,
          fileName: f.originalName || f.fileName || 'unknown',
        })));
      }
    },
  });

  useEffect(() => {
    if (entityId) {
      fetchFiles();
    }
  }, [entityId, fetchFiles]);

  useEffect(() => {
    if (onFilesChange && files) {
      // filesがnull/undefinedの場合は空配列として扱う
      const safeFiles = Array.isArray(files) ? files : [];
      onFilesChange(safeFiles.map(f => ({
        id: f.id,
        url: f.url,
        fileName: f.originalName || f.fileName || 'unknown',
      })));
    }
  }, [files, onFilesChange]);

  const handleDelete = async (file: { id: string }) => {
    try {
      await deleteFile(file.id);
    } catch (err) {
      console.error('Failed to delete file:', err);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13.5" />
          </svg>
          {title}
        </h3>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
          <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
          <button
            onClick={clearError}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {showUpload && (
        <FileUpload
          onUpload={uploadFiles}
          onRemove={(file) => file.id && handleDelete({ id: file.id })}
          accept={accept}
          multiple={multiple}
          maxSize={maxSize}
          maxFiles={maxFiles}
          existingFiles={(Array.isArray(files) ? files : []).map(f => ({
            id: f.id,
            name: f.originalName || f.fileName || 'unknown',
            url: f.url,
            size: f.size,
          }))}
          disabled={disabled || isUploading}
        />
      )}

      {!showUpload && (
        <FileList
          files={(Array.isArray(files) ? files : []).map(f => ({
            id: f.id,
            fileName: f.originalName || f.fileName || 'unknown',
            originalName: f.originalName || f.fileName || 'unknown',
            mimeType: f.mimeType,
            size: f.size,
            url: f.url,
            thumbnailUrl: f.thumbnailUrl,
            createdAt: f.createdAt,
          }))}
          onDelete={showDelete ? handleDelete : undefined}
          showPreview={showPreview}
          showDelete={showDelete}
          showDownload={showDownload}
          layout={layout}
          loading={isLoading}
        />
      )}
    </div>
  );
}
